/**
 * Rewarded Ad Player Component
 *
 * Dual-mode player:
 *   1. When REACT_APP_GAM_AD_TAG_URL is set → loads Google IMA SDK and
 *      plays a real VAST rewarded ad. On COMPLETE, the parent is
 *      responsible for confirming the server-side unlock (via SSV poll).
 *   2. When unset → falls back to the mock self-promo ads below. Useful
 *      for local dev before the GAM account is approved.
 *
 * The IMA path appends compliance flags (tfcd/tfua/npa) and routing
 * params (user_id, custom_data) to the VAST URL so Google's SSV
 * callback can identify the user + feature to unlock.
 *
 * Usage:
 * <RewardedAdPlayer
 *   isOpen={showAd}
 *   onClose={() => setShowAd(false)}
 *   onRewardEarned={() => handleReward()}
 *   adDuration={15}
 *   adPolicy={{tfcd:0, tfua:0, npa:1}}
 *   userId={user.id}
 *   customData={nonce}
 * />
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './RewardedAdPlayer.css';

// Google IMA SDK — loaded dynamically from imasdk.googleapis.com at runtime.
// No npm package exists; the SDK attaches itself to window.google.ima.
const IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
const GAM_AD_TAG_URL = process.env.REACT_APP_GAM_AD_TAG_URL || '';

let imaLoadPromise: Promise<void> | null = null;

function loadImaSdk(): Promise<void> {
    if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
    if ((window as unknown as { google?: { ima?: unknown } }).google?.ima) return Promise.resolve();
    if (imaLoadPromise) return imaLoadPromise;
    imaLoadPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${IMA_SDK_URL}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('IMA SDK load failed')));
            return;
        }
        const script = document.createElement('script');
        script.src = IMA_SDK_URL;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('IMA SDK load failed'));
        document.head.appendChild(script);
    });
    return imaLoadPromise;
}

function buildAdTagUrl(
    base: string,
    params: { tfcd?: 0 | 1; tfua?: 0 | 1; npa?: 0 | 1; userId?: string; customData?: string },
): string {
    const url = new URL(base);
    if (params.tfcd !== undefined) url.searchParams.set('tfcd', String(params.tfcd));
    if (params.tfua !== undefined) url.searchParams.set('tfua', String(params.tfua));
    if (params.npa !== undefined) url.searchParams.set('npa', String(params.npa));
    if (params.userId) url.searchParams.set('rdid', params.userId);
    // Google SSV passes user_id + custom_data back to our backend verbatim.
    if (params.userId) url.searchParams.set('user_id', params.userId);
    if (params.customData) url.searchParams.set('custom_data', params.customData);
    return url.toString();
}

// Mock ad content - replace with real ad tags later
// Translation keys follow pattern: ad.promo.{promoKey}.{title|description|cta}
// See tokens.json files for translations
type PromoKey = 'premium' | 'invite' | 'streak';

interface MockAd {
    id: string;
    promoKey: PromoKey;
    ctaUrl: string;
    bgColor: string;
}

const MOCK_ADS: MockAd[] = [
    {
        id: 'loopy-promo-1',
        promoKey: 'premium',
        ctaUrl: '/pricing',
        bgColor: '#667eea',
    },
    {
        id: 'loopy-promo-2',
        promoKey: 'invite',
        ctaUrl: '/referral',
        bgColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
        id: 'loopy-promo-3',
        promoKey: 'streak',
        ctaUrl: '/tokens',
        bgColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
];

interface RewardedAdPlayerProps {
    isOpen: boolean;
    onClose: () => void;
    onRewardEarned: () => void;
    adDuration?: number; // seconds
    skipAfter?: number; // seconds before skip is available (0 = no skip)
    currentBalance?: number; // current token balance for animation
    rewardAmount?: number; // tokens to be earned (default 3)
    // IMA integration props (all optional — when absent or REACT_APP_GAM_AD_TAG_URL
    // is unset, the component falls back to the mock player below).
    adPolicy?: { tfcd: 0 | 1; tfua: 0 | 1; npa: 0 | 1 };
    userId?: string;
    customData?: string;
}

type AdState = 'loading' | 'playing' | 'completed' | 'error';
type ClaimPhase = 'idle' | 'showing_reward' | 'showing_new_balance' | 'done';

/**
 * Real IMA-backed rewarded ad.
 * Renders a video element + adContainer, boots AdsLoader, and plays the VAST
 * tag. On COMPLETE, invokes onRewardEarned(); on SKIP / error, closes without
 * rewarding. The actual Redis unlock write happens via Google's SSV callback
 * hitting our /api/v1/ads/ssv endpoint — this component just drives the player.
 */
const ImaRewardedPlayer: React.FC<{
    adTagUrl: string;
    onComplete: () => void;
    onClose: () => void;
}> = ({ adTagUrl, onComplete, onClose }) => {
    const { t } = useTranslation('tokens');
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const adsManagerRef = useRef<{ destroy: () => void } | null>(null);

    useEffect(() => {
        let cancelled = false;
        let adDisplayContainer: { initialize: () => void; destroy: () => void } | null = null;

        (async () => {
            try {
                await loadImaSdk();
                if (cancelled) return;
                const w = window as unknown as {
                    google: {
                        ima: {
                            AdDisplayContainer: new (el: HTMLElement, video: HTMLVideoElement) => {
                                initialize: () => void;
                                destroy: () => void;
                            };
                            AdsLoader: new (c: unknown) => {
                                addEventListener: (ev: string, cb: (e: unknown) => void) => void;
                                requestAds: (req: unknown) => void;
                            };
                            AdsRequest: new () => { adTagUrl: string };
                            ViewMode: { NORMAL: string };
                            AdErrorEvent: { Type: { AD_ERROR: string } };
                            AdsManagerLoadedEvent: { Type: { ADS_MANAGER_LOADED: string } };
                            AdEvent: { Type: { COMPLETE: string; SKIPPED: string; ALL_ADS_COMPLETED: string } };
                        };
                    };
                };
                const ima = w.google.ima;

                if (!containerRef.current || !videoRef.current) return;
                adDisplayContainer = new ima.AdDisplayContainer(containerRef.current, videoRef.current);
                adDisplayContainer.initialize();

                const adsLoader = new ima.AdsLoader(adDisplayContainer);
                adsLoader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (e: unknown) => {
                    const msg = (e as { getError?: () => { getMessage: () => string } }).getError?.().getMessage() || 'IMA error';
                    setError(msg);
                });

                adsLoader.addEventListener(
                    ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
                    (e: unknown) => {
                        const getAdsManager = (e as { getAdsManager: (v: HTMLVideoElement) => unknown }).getAdsManager;
                        const manager = getAdsManager(videoRef.current as HTMLVideoElement) as {
                            addEventListener: (ev: string, cb: () => void) => void;
                            init: (w: number, h: number, mode: string) => void;
                            start: () => void;
                            destroy: () => void;
                        };
                        adsManagerRef.current = manager;
                        manager.addEventListener(ima.AdEvent.Type.COMPLETE, () => onComplete());
                        manager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => onComplete());
                        manager.addEventListener(ima.AdEvent.Type.SKIPPED, () => onClose());
                        const w1 = containerRef.current?.clientWidth || 640;
                        const h1 = containerRef.current?.clientHeight || 360;
                        try {
                            manager.init(w1, h1, ima.ViewMode.NORMAL);
                            manager.start();
                        } catch (err) {
                            setError((err as Error).message);
                        }
                    },
                );

                const req = new ima.AdsRequest();
                req.adTagUrl = adTagUrl;
                adsLoader.requestAds(req);
            } catch (err) {
                if (!cancelled) setError((err as Error).message);
            }
        })();

        return () => {
            cancelled = true;
            adsManagerRef.current?.destroy();
            adDisplayContainer?.destroy();
        };
    }, [adTagUrl, onComplete, onClose]);

    if (error) {
        return (
            <div className="ad-error">
                <h2>{t('ad.error', 'Failed to load ad')}</h2>
                <p>{error}</p>
                <button className="ad-close-btn" onClick={onClose}>
                    {t('ad.close', 'Close')}
                </button>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="ima-ad-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
            <video ref={videoRef} playsInline style={{ width: '100%', height: '100%' }} />
        </div>
    );
};

const RewardedAdPlayer: React.FC<RewardedAdPlayerProps> = ({
    isOpen,
    onClose,
    onRewardEarned,
    adDuration = 15,
    skipAfter = 5,
    currentBalance = 0,
    rewardAmount = 3,
    adPolicy,
    userId,
    customData,
}) => {
    const { t } = useTranslation('tokens');
    const [adState, setAdState] = useState<AdState>('loading');
    const [timeRemaining, setTimeRemaining] = useState(adDuration);
    const [canSkip, setCanSkip] = useState(false);
    const [currentAd, setCurrentAd] = useState(MOCK_ADS[0]);
    const [claimPhase, setClaimPhase] = useState<ClaimPhase>('idle');
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const skipTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Select random ad
    const selectRandomAd = useCallback(() => {
        const randomIndex = Math.floor(Math.random() * MOCK_ADS.length);
        setCurrentAd(MOCK_ADS[randomIndex]);
    }, []);

    // Reset state when opened
    useEffect(() => {
        if (isOpen) {
            setAdState('loading');
            setTimeRemaining(adDuration);
            setCanSkip(false);
            setClaimPhase('idle');
            selectRandomAd();

            // Simulate ad loading
            const loadTimer = setTimeout(() => {
                setAdState('playing');
            }, 1000);

            return () => clearTimeout(loadTimer);
        }
    }, [isOpen, adDuration, selectRandomAd]);

    // Countdown timer
    useEffect(() => {
        if (adState === 'playing' && timeRemaining > 0) {
            timerRef.current = setTimeout(() => {
                setTimeRemaining(prev => prev - 1);
            }, 1000);

            return () => {
                if (timerRef.current) clearTimeout(timerRef.current);
            };
        } else if (adState === 'playing' && timeRemaining === 0) {
            setAdState('completed');
        }
    }, [adState, timeRemaining]);

    // Skip availability timer
    useEffect(() => {
        if (adState === 'playing' && skipAfter > 0 && !canSkip) {
            skipTimerRef.current = setTimeout(() => {
                setCanSkip(true);
            }, skipAfter * 1000);

            return () => {
                if (skipTimerRef.current) clearTimeout(skipTimerRef.current);
            };
        }
    }, [adState, skipAfter, canSkip]);

    // Handle ad completion with animation
    const handleComplete = useCallback(() => {
        // Start animation sequence
        setClaimPhase('showing_reward');

        // After showing +5, show new balance
        setTimeout(() => {
            setClaimPhase('showing_new_balance');
        }, 800);

        // After showing new balance, complete and close
        setTimeout(() => {
            setClaimPhase('done');
            onRewardEarned();
            onClose();
        }, 1800);
    }, [onRewardEarned, onClose]);

    // Handle skip (no reward)
    const handleSkip = useCallback(() => {
        onClose();
    }, [onClose]);

    // Handle close during ad (no reward)
    const handleForceClose = useCallback(() => {
        if (adState === 'completed') {
            handleComplete();
        } else {
            onClose();
        }
    }, [adState, handleComplete, onClose]);

    // Handle CTA click - open in new tab so ad continues
    const handleCtaClick = useCallback(() => {
        if (currentAd.ctaUrl) {
            window.open(currentAd.ctaUrl, '_blank', 'noopener,noreferrer');
        }
    }, [currentAd.ctaUrl]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Real IMA mode: only when the env var is configured AND caller supplied
    // SSV routing params (userId + customData). Otherwise fall through to mock.
    if (GAM_AD_TAG_URL && userId && customData) {
        const adTagUrl = buildAdTagUrl(GAM_AD_TAG_URL, {
            tfcd: adPolicy?.tfcd,
            tfua: adPolicy?.tfua,
            npa: adPolicy?.npa,
            userId,
            customData,
        });
        return (
            <div className="rewarded-ad-overlay" onClick={(e) => e.stopPropagation()}>
                <div className="rewarded-ad-container">
                    <ImaRewardedPlayer
                        adTagUrl={adTagUrl}
                        onComplete={() => {
                            onRewardEarned();
                            onClose();
                        }}
                        onClose={onClose}
                    />
                </div>
            </div>
        );
    }

    const progress = ((adDuration - timeRemaining) / adDuration) * 100;
    const skipCountdown = skipAfter - (adDuration - timeRemaining);

    return (
        <div className="rewarded-ad-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="rewarded-ad-container">
                {/* Loading State */}
                {adState === 'loading' && (
                    <div className="ad-loading">
                        <div className="ad-loading-spinner" />
                        <p>{t('ad.loading', 'Loading ad...')}</p>
                    </div>
                )}

                {/* Playing State */}
                {adState === 'playing' && (
                    <>
                        {/* Progress bar */}
                        <div className="ad-progress-bar">
                            <div
                                className="ad-progress-fill"
                                style={{ width: `${progress}%` }}
                            />
                        </div>

                        {/* Timer */}
                        <div className="ad-timer">
                            {timeRemaining}s
                        </div>

                        {/* Mock Ad Content */}
                        <div
                            className="ad-content"
                            style={{ background: currentAd.bgColor }}
                        >
                            <div className="ad-badge">{t('ad.sponsored', 'AD')}</div>

                            <div className="ad-body">
                                <div className="ad-icon">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10"/>
                                        <circle cx="12" cy="12" r="7" strokeOpacity="0.5"/>
                                        <path d="M9 7v10h6" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <h2 className="ad-title">{t(`ad.promo.${currentAd.promoKey}.title`)}</h2>
                                <p className="ad-description">{t(`ad.promo.${currentAd.promoKey}.description`)}</p>
                                <button className="ad-cta-btn" onClick={handleCtaClick}>
                                    {t(`ad.promo.${currentAd.promoKey}.cta`)}
                                </button>
                            </div>

                            <p className="ad-disclaimer">
                                {t('ad.watchToEarn', 'Watch this ad to earn tokens')}
                            </p>
                        </div>

                        {/* Skip button */}
                        <div className="ad-skip-area">
                            {canSkip ? (
                                <button className="ad-skip-btn" onClick={handleSkip}>
                                    {t('ad.skip', 'Skip')} →
                                </button>
                            ) : skipAfter > 0 && skipCountdown > 0 ? (
                                <span className="ad-skip-countdown">
                                    {t('ad.skipIn', 'Skip in')} {skipCountdown}s
                                </span>
                            ) : null}
                        </div>
                    </>
                )}

                {/* Completed State */}
                {adState === 'completed' && (
                    <div className="ad-completed">
                        {claimPhase === 'idle' ? (
                            <>
                                <div className="ad-completed-icon">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <path d="M8 12l3 3 5-6" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <h2>{t('ad.completed', 'Ad Completed!')}</h2>
                                <p>{t('ad.rewardReady', 'Your reward is ready')}</p>
                                <button className="ad-claim-btn" onClick={handleComplete}>
                                    {t('ad.claimReward', 'Claim Reward')}
                                </button>
                            </>
                        ) : (
                            <div className="ad-reward-animation">
                                {/* Token Icon */}
                                <div className="ad-reward-icon">
                                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10"/>
                                        <circle cx="12" cy="12" r="7" strokeOpacity="0.4"/>
                                        <path d="M9 7v10h6" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>

                                {/* Animated Balance Display */}
                                <div className="ad-balance-container">
                                    {/* Current Balance (fades out immediately) */}
                                    <div className="ad-balance-value fade-out">
                                        {currentBalance}
                                    </div>

                                    {/* Reward Amount (+5) */}
                                    <div className={`ad-reward-value ${claimPhase === 'showing_reward' ? 'visible' : ''}`}>
                                        +{rewardAmount}
                                    </div>

                                    {/* New Balance */}
                                    <div className={`ad-new-balance ${claimPhase === 'showing_new_balance' ? 'visible' : ''}`}>
                                        {currentBalance + rewardAmount}
                                    </div>
                                </div>

                                <p className="ad-reward-label">
                                    {claimPhase === 'showing_reward'
                                        ? t('ad.earning', 'Earning tokens...')
                                        : t('ad.newBalance', 'New Balance')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Error State */}
                {adState === 'error' && (
                    <div className="ad-error">
                        <div className="ad-error-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                        </div>
                        <h2>{t('ad.error', 'Failed to load ad')}</h2>
                        <p>{t('ad.tryAgain', 'Please try again later')}</p>
                        <button className="ad-close-btn" onClick={onClose}>
                            {t('ad.close', 'Close')}
                        </button>
                    </div>
                )}

                {/* Close button (only during loading or error) */}
                {(adState === 'loading' || adState === 'error') && (
                    <button className="ad-close-x" onClick={handleForceClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default RewardedAdPlayer;

/**
 * Future Integration Notes:
 *
 * To replace with Google Ad Manager / IMA SDK:
 *
 * 1. Install IMA SDK: npm install @anthropic/ima-wrapper (or use Google's official SDK)
 *
 * 2. Replace MOCK_ADS with real VAST tag URL:
 *    const adTagUrl = 'https://pubads.g.doubleclick.net/gampad/ads?...';
 *
 * 3. Initialize IMA in useEffect:
 *    const adsManager = new google.ima.AdsManager(adDisplayContainer);
 *    adsManager.addEventListener(google.ima.AdEvent.Type.COMPLETE, handleComplete);
 *
 * 4. Add Server-Side Verification (SSV) for security:
 *    - Configure SSV URL in Ad Manager
 *    - Backend verifies Google's callback before awarding tokens
 */
